/**
 * useAuthForm Hook - Form input handling
 */
import { useCallback } from 'react';
import { useAuthContext } from '../context/AuthContext';
import { INPUT_TYPES } from '../types';
import { 
  validateMobileOrEmail, 
  validateEmail,
  validateOtp, 
  validatePassword,
  validateName,
  sanitizeMobileInput, 
  sanitizeOtpInput,
  detectInputType,
} from '../utils/validation';

export function useAuthForm() {
  const {
    identifier,
    identifierType,
    otp,
    password,
    confirmPassword,
    registrationData,
    error,
    setIdentifier,
    setIdentifierType,
    setOtp,
    setPassword,
    setConfirmPassword,
    setRegistrationData,
    setError,
  } = useAuthContext();
  
  /**
   * Handle identifier (mobile/email) input change
   */
  const handleIdentifierChange = useCallback((value) => {
    const type = detectInputType(value);
    
    if (type === INPUT_TYPES.MOBILE) {
      // Sanitize mobile input
      const sanitized = sanitizeMobileInput(value);
      setIdentifier(sanitized);
      
      // Validate and set error
      if (sanitized.length > 0) {
        const validation = validateMobileOrEmail(sanitized);
        if (!validation.isValid && sanitized.length === 10) {
          setError(validation.error);
        } else if (sanitized.length < 10 && !/^[6-9]/.test(sanitized) && sanitized.length > 0) {
          setError('Mobile number must start with 6, 7, 8, or 9');
        } else {
          setError('');
        }
      } else {
        setError('');
      }
    } else {
      // Email input
      setIdentifier(value);
      
      if (value.length > 0) {
        const validation = validateMobileOrEmail(value);
        // Only show error for complete-looking emails
        if (value.includes('@') && value.includes('.')) {
          if (!validation.isValid) {
            setError(validation.error);
          } else {
            setError('');
          }
        } else {
          setError('');
        }
      } else {
        setError('');
      }
    }
    
    setIdentifierType(type);
  }, [setIdentifier, setIdentifierType, setError]);
  
  /**
   * Handle mobile-only input change
   */
  const handleMobileChange = useCallback((value) => {
    // Only allow digits and limit to 10 characters
    const sanitized = sanitizeMobileInput(value);
    setIdentifier(sanitized);
    setIdentifierType(INPUT_TYPES.MOBILE);
    
    // Validate and set error
    if (sanitized.length > 0) {
      if (!/^[6-9]/.test(sanitized)) {
        setError('Mobile number must start with 6, 7, 8, or 9');
      } else if (sanitized.length === 10) {
        const validation = validateMobileOrEmail(sanitized);
        if (!validation.isValid) {
          setError(validation.error);
        } else {
          setError('');
        }
      } else {
        setError('');
      }
    } else {
      setError('');
    }
  }, [setIdentifier, setIdentifierType, setError]);
  
  /**
   * Handle email-only input change
   */
  const handleEmailChange = useCallback((value) => {
    setIdentifier(value);
    setIdentifierType(INPUT_TYPES.EMAIL);
    
    if (value.length > 0) {
      // Use validateEmail directly to avoid numeric-starting emails being
      // misclassified as mobile numbers by detectInputType.
      const validation = validateEmail(value);
      // Only show error for complete-looking emails
      if (value.includes('@') && value.includes('.')) {
        if (!validation.isValid) {
          setError(validation.error);
        } else {
          setError('');
        }
      } else {
        setError('');
      }
    } else {
      setError('');
    }
  }, [setIdentifier, setIdentifierType, setError]);
  
  /**
   * Handle OTP input change
   */
  const handleOtpChange = useCallback((value) => {
    const sanitized = sanitizeOtpInput(value);
    setOtp(sanitized);
    
    if (sanitized.length > 0 && sanitized.length < 6) {
      setError('OTP must be 6 digits');
    } else {
      setError('');
    }
  }, [setOtp, setError]);
  
  /**
   * Handle password input change
   */
  const handlePasswordChange = useCallback((value) => {
    setPassword(value);
    setError('');
  }, [setPassword, setError]);
  
  /**
   * Handle confirm password input change
   */
  const handleConfirmPasswordChange = useCallback((value) => {
    setConfirmPassword(value);
    
    if (value && value !== password) {
      setError('Passwords do not match');
    } else {
      setError('');
    }
  }, [password, setConfirmPassword, setError]);
  
  /**
   * Handle registration form field change
   */
  const handleRegistrationChange = useCallback((field, value) => {
    setRegistrationData(prev => ({
      ...prev,
      [field]: value,
    }));
    setError('');
  }, [setRegistrationData, setError]);
  
  /**
   * Validate registration form
   */
  const validateRegistrationForm = useCallback(() => {
    const errors = {};
    
    const firstNameValidation = validateName(registrationData.firstName, 'First name');
    if (!firstNameValidation.isValid) {
      errors.firstName = firstNameValidation.error;
    }
    
    const lastNameValidation = validateName(registrationData.lastName, 'Last name');
    if (!lastNameValidation.isValid) {
      errors.lastName = lastNameValidation.error;
    }
    
    if (!registrationData.mobile) {
      errors.mobile = 'Please enter mobile number';
    }
    
    if (!registrationData.dob) {
      errors.dob = 'Please select date of birth';
    }
    
    if (!registrationData.salutation) {
      errors.salutation = 'Please select salutation';
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  }, [registrationData]);
  
  /**
   * Check if identifier is valid for submission
   */
  const isIdentifierValid = useCallback(() => {
    const validation = validateMobileOrEmail(identifier);
    return validation.isValid;
  }, [identifier]);
  
  /**
   * Check if OTP is valid for submission
   */
  const isOtpValid = useCallback(() => {
    const validation = validateOtp(otp);
    return validation.isValid;
  }, [otp]);
  
  /**
   * Check if password meets requirements
   */
  const getPasswordValidation = useCallback(() => {
    return validatePassword(password);
  }, [password]);
  
  /**
   * Check if passwords match
   */
  const doPasswordsMatch = useCallback(() => {
    return password === confirmPassword && password.length > 0;
  }, [password, confirmPassword]);
  
  return {
    // Values
    identifier,
    identifierType,
    otp,
    password,
    confirmPassword,
    registrationData,
    error,
    
    // Setters (exposed for manual control)
    setIdentifier,
    setIdentifierType,
    setError,
    
    // Handlers
    handleIdentifierChange,
    handleMobileChange,
    handleEmailChange,
    handleOtpChange,
    handlePasswordChange,
    handleConfirmPasswordChange,
    handleRegistrationChange,
    
    // Validators
    isIdentifierValid,
    isOtpValid,
    getPasswordValidation,
    doPasswordsMatch,
    validateRegistrationForm,
  };
}
